import { useLocation } from 'react-router-dom';
import { RecordType } from '@xbcb/shared-types';

/**
 * Hook to determine if Katal components should be used instead of AntD components.
 *
 * This hook implements a gradual migration strategy from AntD to Katal Design System
 * by enabling Katal components on specific routes and record types. This allows for
 * controlled rollout and testing of Katal components without affecting the entire
 * application at once.
 *
 * The feature flag is based on:
 * 1. Current route path - enables Katal for specific pages
 * 2. Record type context - enables Katal for specific business entities
 *
 * @param {RecordType} recordType - Optional record type to check for Katal enablement
 * @return {boolean} - true if Katal components should be used, false for AntD
 *
 * @example
 * ```typescript
 * // In a component that needs to choose between AntD and Katal
 * const useKatal = useKatalFeatureFlag(RecordType.IMPORTER_ENTITY_ACTIVATION);
 *
 * if (useKatal) {
 *   return <KatalComponent />;
 * } else {
 *   return <AntDComponent />;
 * }
 * ```
 *
 * @example
 * ```typescript
 * // In a route-based component
 * const useKatal = useKatalFeatureFlag();
 *
 * if (useKatal) {
 *   return <KatalTabs />;
 * } else {
 *   return <AntdTabs />;
 * }
 * ```
 */
export const useKatalFeatureFlag = (recordType?: RecordType): boolean => {
  const location = useLocation();

  // Routes where Katal components are enabled
  const KATAL_ENABLED_ROUTES = [
    '/importer-entity-activation',
    // Add more routes as they are converted to Katal
  ];

  // Record types where Katal components are enabled
  const KATAL_ENABLED_RECORD_TYPES: RecordType[] = [
    RecordType.IMPORTER_ENTITY_ACTIVATION,
    // Add more record types as they are converted to Katal
  ];

  // Check if current route is enabled for Katal
  const isRouteEnabled = KATAL_ENABLED_ROUTES.some((route) =>
    location.pathname.includes(route),
  );

  // Check if record type is enabled for Katal
  const isRecordTypeEnabled = recordType
    ? KATAL_ENABLED_RECORD_TYPES.includes(recordType)
    : false;

  // Return true if either route or record type is enabled
  return isRouteEnabled || isRecordTypeEnabled;
};

export default useKatalFeatureFlag;
